import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { trackCxFoundationsContactFormCloseClick } from "@faire/web-api--source/events/cxFoundations/click/contactFormClose";
import { trackCxFoundationsHowCanIHelpToHelpCenterNextClick } from "@faire/web-api--source/events/cxFoundations/click/howCanIHelpToHelpCenterNext";
import { trackCxFoundationsINeedMoreHelpClick } from "@faire/web-api--source/events/cxFoundations/click/iNeedMoreHelp";
import get from "lodash/get";
import omit from "lodash/omit";
import qs from "query-string";
import { useHistory, useLocation } from "react-router-dom";

import { EVENT, STEP } from "../consts";

import { useLoggedOutContactUsContextStore } from "./LoggedOutContactUsContextStore";
import { useLoggedOutIssuesDropdown } from "./useLoggedOutIssuesDropdown";
import { useLoggedOutIssueType } from "./useLoggedOutIssueType";

type FeedbackByIssueType = { [key: string]: boolean };

export const useLoggedOutContactUsNavigation = () => {
  const location = useLocation();
  const history = useHistory();
  const [feedbackCollectedByIssueType, setFeedbackCollectedByIssueType] =
    useState<FeedbackByIssueType>({});
  const [{ currentStep }, setLoggedOutContactUsContextStore] =
    useLoggedOutContactUsContextStore(["currentStep"]);
  const { issueL1, issueL2, issueL3, showDropdownL2, showDropdownL3 } =
    useLoggedOutIssuesDropdown();
  const { issueType } = useLoggedOutIssueType();
  let eventPage: EVENT = EVENT.HOW_CAN_WE_HELP_PAGE;

  const nextBtnDisabled = () => {
    if (!issueL1) {
      return true;
    }
    if (showDropdownL2() && !issueL2) {
      return true;
    }
    if (showDropdownL3() && !issueL3) {
      return true;
    }
    return false;
  };

  const isFirstStep = currentStep === STEP.LOGGED_OUT_SELECT_TOPIC;

  const isLastStep = currentStep === STEP.CONFIRMATION;

  const goToNextStep = () => {
    switch (currentStep) {
      case STEP.LOGGED_OUT_SELECT_TOPIC:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.HELP_CENTER_ARTICLE,
        });
        eventPage = EVENT.DOES_THIS_HELP_PAGE;
        trackCxFoundationsHowCanIHelpToHelpCenterNextClick(
          issueType,
          getRetailerToken() ?? "",
          getBrandToken() ?? ""
        );
        break;
      case STEP.HELP_CENTER_ARTICLE:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.LOGGED_OUT_CONTACT_US,
        });
        eventPage = EVENT.CONTACT_US_PAGE;
        trackCxFoundationsINeedMoreHelpClick(
          issueType,
          getRetailerToken() ?? "",
          getBrandToken() ?? ""
        );
        break;
      case STEP.LOGGED_OUT_CONTACT_US:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.CONFIRMATION,
        });
        eventPage = EVENT.CONFIRMATION_PAGE;
        break;
      default:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.LOGGED_OUT_SELECT_TOPIC,
        });
        eventPage = EVENT.HOW_CAN_WE_HELP_PAGE;
        break;
    }
  };

  const goToPrevStep = () => {
    switch (currentStep) {
      case STEP.LOGGED_OUT_SELECT_TOPIC:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.LOGGED_OUT_SELECT_TOPIC,
        });

        eventPage = EVENT.HOW_CAN_WE_HELP_PAGE;
        break;
      case STEP.HELP_CENTER_ARTICLE:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.LOGGED_OUT_SELECT_TOPIC,
        });

        eventPage = EVENT.HOW_CAN_WE_HELP_PAGE;
        break;
      case STEP.LOGGED_OUT_CONTACT_US:
        if (nextBtnDisabled()) {
          setLoggedOutContactUsContextStore({
            currentStep: STEP.LOGGED_OUT_SELECT_TOPIC,
          });

          eventPage = EVENT.HOW_CAN_WE_HELP_PAGE;
        } else {
          setLoggedOutContactUsContextStore({
            currentStep: STEP.HELP_CENTER_ARTICLE,
          });

          eventPage = EVENT.DOES_THIS_HELP_PAGE;
        }
        break;
      default:
        setLoggedOutContactUsContextStore({
          currentStep: STEP.LOGGED_OUT_SELECT_TOPIC,
        });
        eventPage = EVENT.HOW_CAN_WE_HELP_PAGE;
        break;
    }
  };

  const removeContactQueryParam = () => {
    const { parse, stringify } = qs;
    const pathname = get(location, "pathname");
    const query = parse(get(location, "search", ""));
    const omitParams = stringify(omit(query, ["contact", "loggedOut"]));
    const queryParams = omitParams ? `?${omitParams}` : omitParams;

    history.push(`${pathname}${queryParams}`);
  };

  const setFeedbackCollected = (key: string, value: boolean) => {
    setFeedbackCollectedByIssueType({
      ...feedbackCollectedByIssueType,
      [key]: value,
    });
  };

  const closeModal = () => {
    removeContactQueryParam();
    setFeedbackCollectedByIssueType({});
    trackCxFoundationsContactFormCloseClick(issueType, "", eventPage);
  };

  return {
    nextBtnDisabled,
    setFeedbackCollected,
    feedbackCollectedByIssueType,
    closeModal,
    goToNextStep,
    goToPrevStep,
    currentStep,
    isLastStep,
    isFirstStep,
  };
};
